import React from 'react';
import { useStagger, useInView } from '../../hooks/useInView';

const steps = [
    {
        number: "01",
        title: "Discovery",
        description: "Nos sumergimos en tu visión y objetivos. Entendemos tu negocio para diseñar la mejor estrategia técnica.",
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
        ),
    },
    {
        number: "02",
        title: "Arquitectura",
        description: "Diseñamos el stack técnico y el roadmap. Todo documentado y validado con vos antes de escribir una línea.",
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
    },
    {
        number: "03",
        title: "Desarrollo por sprints",
        description: "Ciclos cortos con entregas incrementales. Feedback continuo y ajustes en tiempo real, sin sorpresas al final.",
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
        ),
    },
    {
        number: "04",
        title: "Escalamiento",
        description: "Lanzamos, medimos y optimizamos. Acompañamos el crecimiento con soporte, métricas y mejoras continuas.",
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
        ),
    },
];

const commitments = [
    "Sin costos ocultos",
    "Contratos claros, sin letra chica",
    "El código y los datos son tuyos",
    "Reuniones semanales de avance",
    "Entregas incrementales visibles",
    "Comunicación directa, siempre",
];

const ICON_SHIELD = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const cell = "bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40 rounded-2xl transition-colors duration-300";

export default function ProcesoTransparencia() {
    const { ref: headRef, itemStyle: headStyle } = useStagger({ count: 2, stagger: 70 });
    const { ref: stepsRef, itemStyle } = useStagger({ count: 4, stagger: 75 });
    const [cardRef, cardInView] = useInView({ threshold: 0.1 });

    return (
        <section className="py-28 px-4 bg-white dark:bg-[#0d0d0d]">
            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div ref={headRef} className="mb-12">
                    <p style={headStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Proceso · Transparencia</p>
                    <h2 style={headStyle(1)} className="text-3xl font-thin tracking-tighter sm:text-4xl text-[#0b2b40] dark:text-white">
                        Cómo <span className="text-gradient text-short">trabajamos</span>
                    </h2>
                </div>

                {/* Grid: steps 2/3 + transparency 1/3 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                    {/* Steps — 2×2 subgrid */}
                    <div ref={stepsRef} className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {steps.map((step, i) => (
                            <div key={step.number} style={itemStyle(i)} className={`${cell} p-7 flex flex-col`}>
                                <div className="flex items-center justify-between mb-5">
                                    <span className="text-5xl font-bold text-gray-100 dark:text-white/[0.05] select-none leading-none">
                                        {step.number}
                                    </span>
                                    <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/10 border border-[#11b36d]/20 text-[#11b36d]">
                                        {step.icon}
                                    </div>
                                </div>
                                <h3 className="text-base font-medium text-[#0b2b40] dark:text-white mb-2">
                                    {step.title}
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Transparency card — full right column height */}
                    <div
                        ref={cardRef}
                        style={{
                            opacity: cardInView ? 1 : 0,
                            transform: cardInView ? 'translateX(0)' : 'translateX(28px)',
                            transition: 'opacity 0.65s cubic-bezier(0.23,1,0.32,1) 0.1s, transform 0.65s cubic-bezier(0.23,1,0.32,1) 0.1s, border-color 0.3s ease',
                        }}
                        className="bg-[#f4fdf8] dark:bg-[#0b1a10] border border-[#11b36d]/40 hover:border-[#11b36d]/65 rounded-2xl p-7 flex flex-col shadow-[0_0_28px_rgba(17,179,109,0.07)] dark:shadow-[0_0_32px_rgba(17,179,109,0.12)]"
                    >

                        <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/15 border border-[#11b36d]/30 text-[#11b36d] mb-4 w-fit">
                            {ICON_SHIELD}
                        </div>

                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">
                            Sin letra chica
                        </p>
                        <h3 className="text-xl font-medium text-[#0b2b40] dark:text-white mb-3">
                            Transparencia total
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
                            Sabés exactamente qué recibís, cuánto pagás y cuándo lo pagás. Sin sorpresas, en cada etapa.
                        </p>

                        <ul className="space-y-3 flex-grow">
                            {commitments.map((item) => (
                                <li key={item} className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#11b36d]/15 border border-[#11b36d]/30 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-[#11b36d]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-6 pt-5 border-t border-[#11b36d]/15">
                            <p className="text-xs text-slate-400 dark:text-slate-500 italic leading-relaxed">
                                "Todos nuestros contratos incluyen documentación clara y acceso total al repositorio desde el día uno."
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}
